export type TokenType =
  | 'AND' | 'OR' | 'NOT' | 'IN'
  | 'LPAREN' | 'RPAREN'
  | 'COMMA'
  | 'TRUE' | 'FALSE'
  | 'IDENTIFIER'
  | 'STRING'
  | 'NUMBER'
  | 'EQ' | 'NEQ'
  | 'EOF';

export interface Token {
  type: TokenType;
  value: string;
}

const KEYWORDS: Record<string, TokenType> = {
  and: 'AND',
  or: 'OR',
  not: 'NOT',
  in: 'IN',
  True: 'TRUE',
  False: 'FALSE',
};

export function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const len = input.length;

  while (i < len) {
    const ch = input[i];

    // Skip whitespace
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    // Single-char tokens
    if (ch === '(') { tokens.push({ type: 'LPAREN', value: '(' }); i++; continue; }
    if (ch === ')') { tokens.push({ type: 'RPAREN', value: ')' }); i++; continue; }
    if (ch === ',') { tokens.push({ type: 'COMMA', value: ',' }); i++; continue; }

    // == and !=
    if (ch === '=' && input[i + 1] === '=') {
      tokens.push({ type: 'EQ', value: '==' });
      i += 2;
      continue;
    }
    if (ch === '!' && input[i + 1] === '=') {
      tokens.push({ type: 'NEQ', value: '!=' });
      i += 2;
      continue;
    }

    // String literal (single-quoted)
    if (ch === "'") {
      let str = '';
      i++; // skip opening quote
      while (i < len && input[i] !== "'") {
        str += input[i];
        i++;
      }
      i++; // skip closing quote
      tokens.push({ type: 'STRING', value: str });
      continue;
    }

    // Number
    if (ch >= '0' && ch <= '9') {
      let num = '';
      while (i < len && input[i] >= '0' && input[i] <= '9') {
        num += input[i];
        i++;
      }
      tokens.push({ type: 'NUMBER', value: num });
      continue;
    }

    // Identifier or keyword
    if (isIdentStart(ch)) {
      let ident = '';
      while (i < len && isIdentChar(input[i])) {
        ident += input[i];
        i++;
      }
      const kw = KEYWORDS[ident];
      if (kw) {
        tokens.push({ type: kw, value: ident });
      } else {
        tokens.push({ type: 'IDENTIFIER', value: ident });
      }
      continue;
    }

    // Skip unknown characters
    i++;
  }

  tokens.push({ type: 'EOF', value: '' });
  return tokens;
}

function isIdentStart(ch: string): boolean {
  return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_';
}

function isIdentChar(ch: string): boolean {
  return isIdentStart(ch) || (ch >= '0' && ch <= '9');
}
